import classNames from 'classnames';
import { useScroll } from 'framer-motion';
import { useRef } from 'react';
import { ImageShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import Button from '../../shared/Button';
import GradientBG from '../../shared/GradientBG';
import Image from '../../shared/Image';
import Link from '../../shared/Link';
import Typografed from '../../shared/Typografed';

export type ThemedTracksSectionProps = {
    title?: string;
    description?: string;
    tracks?: {
        title: string;
        image?: ImageShape | null;
    }[];
    stats?: {
        value: string;
        label: string;
    }[];
    link?: {
        name: string;
        href: string;
    } | null;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    themedTracksSectionProps?: ThemedTracksSectionProps;
    withGradientBG?: boolean;
};

function ThemedTracksSectionGradientBG({ sectionRef }: { sectionRef: React.RefObject<HTMLDivElement | null> }) {
    const { scrollYProgress } = useScroll({ target: sectionRef });

    return <GradientBG scrollYProgress={scrollYProgress} />;
}

function ThemedTracksSection({ themedTracksSectionProps, withGradientBG = true, className, ...props }: Props) {
    const ref = useRef<HTMLDivElement>(null);
    return (
        <div
            {...props}
            className={classNames('themed-tracks__section js-header-theme-trigger', className)}
            id={props.id}
            ref={ref}
        >
            {withGradientBG && <ThemedTracksSectionGradientBG sectionRef={ref} />}

            <div className="themed-tracks__wrapper wrapper">
                <div className="themed-tracks-header">
                    <div className="themed-tracks-header-left">
                        {themedTracksSectionProps?.title && (
                            <h2 className="themed-tracks-header-left-title h3">
                                <Typografed>{themedTracksSectionProps.title}</Typografed>
                            </h2>
                        )}
                        {themedTracksSectionProps?.link && (
                            <Button
                                tag={Link}
                                href={themedTracksSectionProps.link.href}
                                variant="outline-light"
                                className="text-xs themed-tracks-header-link"
                                target="_blank"
                                rel="noreferrer nofollow"
                            >
                                <Typografed>{themedTracksSectionProps.link.name}</Typografed>
                            </Button>
                        )}
                    </div>
                    <div className="themed-tracks-header-right">
                        {themedTracksSectionProps?.description && (
                            <div
                                className="themed-tracks__descr"
                                dangerouslySetInnerHTML={{ __html: tp(themedTracksSectionProps.description) }}
                            ></div>
                        )}
                        {themedTracksSectionProps?.stats && themedTracksSectionProps.stats.length > 0 && (
                            <div className="themed-tracks__numbers-list">
                                {themedTracksSectionProps.stats.map((stat) => (
                                    <div className="themed-tracks__numbers-item" key={stat.label}>
                                        <div className="themed-tracks__numbers-item-title number-big number-stroke">
                                            <Typografed>{stat.value}</Typografed>
                                        </div>
                                        <div className="themed-tracks__numbers-item-descr">
                                            <Typografed>{stat.label}</Typografed>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                {themedTracksSectionProps?.tracks && themedTracksSectionProps.tracks.length > 0 && (
                    <div className="themed-tracks__list-wrapper">
                        <ul className="themed-tracks__list list-unstyled">
                            {themedTracksSectionProps.tracks.map((track) => (
                                <li className="themed-tracks__item-outer" key={track.title}>
                                    <div className="themed-tracks__item-inner">
                                        <div className="themed-tracks__item-inner-title text-xl">
                                            <Typografed>{track.title}</Typografed>
                                        </div>
                                        {track.image?.src && (
                                            <div className="themed-tracks__item-inner-image">
                                                <Image
                                                    src={track.image.src}
                                                    alt={track.image.alt ?? track.title}
                                                    width={track.image.width ?? 100}
                                                    height={track.image.height ?? 100}
                                                    sizes="35vw"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ThemedTracksSection;
